import { Button, Form, InputNumber, message, Table } from '@maxtropy/components';
import {
  apiV2MicrogridTopologyLoadParamDetailPost,
  apiV2MicrogridTopologyLoadParamSavePost,
} from '@maxtropy/dmes-apis-v2';
import { useRequest } from 'ahooks';
import { Space } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import { PermissionsType } from '@/common/permissionsConst';
import { useHasPermission } from '@/utils/hooks';
import EditLoadSideParamModal from './EditLoadSideParamModal';
import { getLoadAssetLabel } from './constants';
import { LoadBaselineTargetFormValues, LoadSideParamRecord } from './types';

export interface LoadSideParamsTabRef {
  save: () => Promise<void>;
  isEditing: boolean;
  loading: boolean;
}

const renderEmpty = (value?: number | string) => value ?? '--';

const LoadSideParamsTab = forwardRef<LoadSideParamsTabRef>((_, ref) => {
  const hasSetupPermission = useHasPermission(PermissionsType.B_LOAD_SIDE_PARAMETER_CONFIGURATION);
  const [form] = Form.useForm<LoadBaselineTargetFormValues>();
  const [isEditing, setIsEditing] = useState(false);
  const [dataSource, setDataSource] = useState<LoadSideParamRecord[]>([]);
  const [editingRecord, setEditingRecord] = useState<LoadSideParamRecord>();
  const { refreshAsync: refreshLoadParamDetail, loading: detailLoading } = useRequest(() =>
    apiV2MicrogridTopologyLoadParamDetailPost({}).then(res => {
      setDataSource(
        res.loadParamList?.map((item, index) => {
          const detailItem = item as typeof item & {
            loadObjectName?: string;
            deviceName?: string;
            name?: string;
          };

          return {
            id: item.id ?? item.loadObjectId ?? Date.now() + index,
            loadObjectId: item.loadObjectId,
            deviceName:
              detailItem.loadObjectName ??
              detailItem.deviceName ??
              detailItem.name ??
              dataSource.find(record => record.loadObjectId === item.loadObjectId)?.deviceName ??
              String(item.loadObjectId ?? ''),
            minRunningLoad: item.minRunningLoad,
            maxLowLoadRunningHour: item.maxMinLoadDuration,
            maxLoadDownRate: item.maxDropRate,
            maxLoadUpRate: item.maxRaiseRate,
            assetInfo: item.assetInfo === undefined || item.assetInfo === null ? '' : String(item.assetInfo),
          };
        }) ?? []
      );
      form.setFieldsValue({ loadBaselineTarget: res.loadBaselineTarget });
    })
  );
  const { runAsync: saveLoadParam, loading: saving } = useRequest(apiV2MicrogridTopologyLoadParamSavePost, {
    manual: true,
  });

  const columns: ColumnsType<LoadSideParamRecord> = useMemo(
    () => [
      {
        title: '负荷设备名称',
        dataIndex: 'deviceName',
      },
      {
        title: '最低运行负荷(MW)',
        dataIndex: 'minRunningLoad',
        render: renderEmpty,
      },
      {
        title: '最长低负荷运行时间(h)',
        dataIndex: 'maxLowLoadRunningHour',
        render: renderEmpty,
      },
      {
        title: '最快降负荷速率(MW/min)',
        dataIndex: 'maxLoadDownRate',
        render: renderEmpty,
      },
      {
        title: '最快升负荷速率(MW/min)',
        dataIndex: 'maxLoadUpRate',
        render: renderEmpty,
      },
      {
        title: '负荷设备资产信息',
        dataIndex: 'assetInfo',
        render: value => renderEmpty(getLoadAssetLabel(value) ?? value),
      },
      {
        title: '操作',
        width: 100,
        render: (_, record) => (
          <Button type="link" htmlType="button" disabled={!isEditing} onClick={() => setEditingRecord(record)}>
            编辑
          </Button>
        ),
      },
    ],
    [isEditing]
  );

  const handleModalOk = (values: LoadSideParamRecord) => {
    setDataSource(list => list.map(item => (item.id === values.id ? values : item)));
    setEditingRecord(undefined);
  };

  const handleSave = async () => {
    const values = await form.validateFields();
    const incompleteRecord = dataSource.find(
      item =>
        item.minRunningLoad === undefined ||
        item.maxLowLoadRunningHour === undefined ||
        item.maxLoadDownRate === undefined ||
        item.maxLoadUpRate === undefined ||
        !item.assetInfo
    );

    if (incompleteRecord) {
      message.warning(`请完善${incompleteRecord.deviceName}参数配置`);
      return;
    }

    await saveLoadParam({
      loadParamList: dataSource.map(item => ({
        loadObjectId: item.loadObjectId ?? item.id,
        minRunningLoad: item.minRunningLoad!,
        maxMinLoadDuration: item.maxLowLoadRunningHour!,
        maxDropRate: item.maxLoadDownRate!,
        maxRaiseRate: item.maxLoadUpRate!,
        assetInfo: item.assetInfo,
      })),
      loadBaselineTarget: values.loadBaselineTarget,
    });
    await refreshLoadParamDetail();
    message.success('保存成功');
    setIsEditing(false);
  };

  const handleCancel = async () => {
    await refreshLoadParamDetail();
    setIsEditing(false);
  };

  useImperativeHandle(ref, () => ({
    save: handleSave,
    isEditing,
    loading: detailLoading || saving,
  }));

  return (
    <>
      <Form form={form} layout="horizontal" disabled={!isEditing}>
        <Table rowKey="id" loading={detailLoading} pagination={false} columns={columns} dataSource={dataSource} />
        <Form.Item
          label="负荷侧基线目标"
          name="loadBaselineTarget"
          style={{ maxWidth: 520, marginTop: 16 }}
          rules={[{ type: 'number', min: 0, max: 99999999.99, message: '请输入0~99999999.99' }]}
        >
          <InputNumber
            placeholder="请输入"
            addonAfter="MW"
            min={0}
            max={99999999.99}
            precision={2}
            style={{ width: '100%' }}
          />
        </Form.Item>
      </Form>
      <Space size={8} className="sticky-footer" style={{ zIndex: 99, paddingLeft: 0, marginTop: 32 }}>
        {isEditing ? (
          <>
            <Button type="primary" htmlType="button" loading={saving} onClick={handleSave}>
              保存
            </Button>
            <Button htmlType="button" onClick={handleCancel}>
              取消
            </Button>
          </>
        ) : (
          hasSetupPermission && (
            <Button type="primary" htmlType="button" onClick={() => setIsEditing(true)}>
              编辑
            </Button>
          )
        )}
      </Space>
      <EditLoadSideParamModal
        open={!!editingRecord}
        record={editingRecord}
        onCancel={() => setEditingRecord(undefined)}
        onOk={handleModalOk}
      />
    </>
  );
});

export default LoadSideParamsTab;
